import React, { useEffect, useMemo, useRef, useState } from 'react';
import { vekaletnameAPI } from '../../api/api';
import './TazminatDosyaDoldur.css';

/*
  Tazminat hesabının üstündeki "Dosyadan doldur" kartı.

  Dosya seçilince:
    - hesap türü dava türünden (vefat → destek, maluliyet → iş gücü kaybı),
    - kaza türü (iş kazası / trafik), olay tarihi, ad, son ücret dosyadan,
    - müvekkilin doğum tarihi dosyadan; dosyada yoksa vekaletnameden (OCR,
      sunucuda) okunur. Vekaletnameden okunan bilgi dosyaya YAZILMAZ.
  Destek tazminatında müvekkil çoğu zaman kazalının yakınıdır; doğum tarihi
  seçilen yakınlığa (eş / anne / baba / çocuk) yazılır.

  Seçilen dosya, hesabın PDF olarak aktarılacağı dosya olarak da seçilir.
*/

const VEFAT = ['DestekYoksunKalma', 'TrafikKazasiVefat', 'IsKazasiVefat', 'TrafikIsKazasiVefat', 'IdariDavaVefat',
  12, 14, 19, 22, 27];
const IS_KAZASI = ['IsKazasiVefat', 'IsKazasiMaluliyet', 'TrafikIsKazasiMaluliyet', 'TrafikIsKazasiVefat', 19, 20, 21, 22];
const TAZMINAT_TURLERI = ['TrafikKazasi', 'GeciciİsGormezlik', 'DestekYoksunKalma', 'TrafikKazasiMaluliyet', 'TrafikKazasiVefat',
  'MaddiTazminat', 'IsKazasiVefat', 'IsKazasiMaluliyet', 'TrafikIsKazasiMaluliyet', 'TrafikIsKazasiVefat', 'IdariDavaVefat',
  'IdariDavaYaralanma', 'ElektrikCarpmasi', 'MaluliyetTespit', 'Malpraktis', 'MaddiManeviTazminat',
  1, 3, 12, 13, 14, 15, 19, 20, 21, 22, 27, 28, 29, 35, 39, 46];

const al = (d, ad) => d?.[ad] ?? d?.[ad.charAt(0).toLowerCase() + ad.slice(1)];
const gun = (v) => (v ? String(v).slice(0, 10) : '');
const trTarih = (iso) => (iso ? iso.split('-').reverse().join('.') : '');

/** "12.03.1985" / "12/3/1985" → "1985-03-12" */
const isoYap = (s) => {
  const m = String(s || '').match(/(\d{1,2})[./-](\d{1,2})[./-](\d{4})/);
  if (!m) return '';
  return `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`;
};

const katla = (s) => String(s ?? '').toLocaleLowerCase('tr').normalize('NFD').replace(/[̀-ͯ]/g, '');

const YAKINLIK = [
  { id: 'es', ad: 'Eşi' },
  { id: 'cocuk', ad: 'Çocuğu' },
  { id: 'anne', ad: 'Annesi' },
  { id: 'baba', ad: 'Babası' },
];

const TazminatDosyaDoldur = ({ dosyalar, onDoldur, onDosyaSec, baslangicId = '' }) => {
  const [ara, setAra] = useState('');
  const [acik, setAcik] = useState(false);
  const [secili, setSecili] = useState(null);
  const [yakinlik, setYakinlik] = useState('es');
  const [durum, setDurum] = useState(null);

  const sonuclar = useMemo(() => {
    const q = katla(ara.trim());
    const liste = (dosyalar || []).filter((d) => !q
      || String(al(d, 'FoyNo') ?? '').includes(ara.trim())
      || katla(al(d, 'Muvekkil')).includes(q));
    return liste
      .sort((a, b) => {
        const ta = TAZMINAT_TURLERI.includes(al(a, 'DavaTuru')) ? 1 : 0;
        const tb = TAZMINAT_TURLERI.includes(al(b, 'DavaTuru')) ? 1 : 0;
        return tb - ta || Number(al(b, 'FoyNo') || 0) - Number(al(a, 'FoyNo') || 0);
      })
      .slice(0, 8);
  }, [dosyalar, ara]);

  /** Yakınlık değişince eski yere yazılmış doğum tarihini siler. */
  const yakinlikTemizle = (yak) => ({
    es: { esVar: false, esDogum: '' },
    anne: { anneVar: false, anneDogum: '' },
    baba: { babaVar: false, babaDogum: '' },
    cocuk: { cocuklar: [{ dogumTarihi: '', cinsiyet: 'E', yuksekOgrenim: false }] },
  }[yak] || {});

  /** Doğum tarihini hesaptaki doğru kişiye yazar. */
  const dogumuYaz = (mod, yak, dogum) => {
    if (!dogum) return {};
    if (mod !== 'destek') return { dogumTarihi: dogum };
    if (yak === 'es') return { esVar: true, esDogum: dogum };
    if (yak === 'anne') return { anneVar: true, anneDogum: dogum };
    if (yak === 'baba') return { babaVar: true, babaDogum: dogum };
    if (yak === 'cocuk') return { cocuklar: [{ dogumTarihi: dogum, cinsiyet: 'E', yuksekOgrenim: false }] };
    return {};
  };

  const sec = async (d) => {
    setAcik(false);
    setAra('');
    const id = al(d, 'Id');
    const davaTuru = al(d, 'DavaTuru');
    const mod = VEFAT.includes(davaTuru) ? 'destek' : 'isgucu';
    const kazaTuru = IS_KAZASI.includes(davaTuru) ? 'is' : 'trafik';
    const olay = gun(al(d, 'KazaTarihi'));
    let dogum = gun(al(d, 'MuvekkilDogumTarihi'));
    const sonUcret = al(d, 'SonUcret');
    const kayit = { id, foyNo: al(d, 'FoyNo'), muvekkil: al(d, 'Muvekkil'), mod, dogum, dogumKaynak: dogum ? 'dosya' : '' };
    setSecili(kayit);
    onDosyaSec?.(String(id));

    // Önceki dosyadan kalan kişi bilgileri temizlenir; bu dosyanın bilgileri yazılır.
    const guncelle = {
      adSoyad: '', dogumTarihi: '', aylikUcret: '',
      esVar: false, esDogum: '', anneVar: false, anneDogum: '', babaVar: false, babaDogum: '',
      cocuklar: [{ dogumTarihi: '', cinsiyet: 'E', yuksekOgrenim: false }],
      mod,
      kazaTuru,
      ...(olay ? { olayTarihi: olay } : {}),
      ...(mod === 'isgucu' ? { adSoyad: al(d, 'Muvekkil') || '' } : {}),
      ...(sonUcret ? { aylikUcret: String(sonUcret) } : {}),
      ...dogumuYaz(mod, yakinlik, dogum),
    };
    onDoldur(guncelle);

    if (dogum) { setDurum(null); return; }

    // Doğum tarihi dosyada yok: vekaletnameyi oku (kaydetmeden).
    setDurum({ tip: 'okunuyor', metin: 'Doğum tarihi dosyada yok; vekaletname okunuyor… (10–30 sn)' });
    try {
      const s = await vekaletnameAPI.oku(id);
      const b = s?.bilgi || s?.Bilgi || {};
      dogum = isoYap(b.dogumTarihi ?? b.DogumTarihi);
      if (s?.okundu && dogum) {
        onDoldur(dogumuYaz(mod, yakinlik, dogum));
        setSecili((o) => ({ ...o, dogum, dogumKaynak: 'vekaletname' }));
        setDurum({ tip: 'tamam', metin: `Vekaletnameden okundu: doğum tarihi ${trTarih(dogum)}${s.belgeAdi ? ` (${s.belgeAdi})` : ''}. Kontrol edin.` });
      } else {
        setDurum({ tip: 'uyari', metin: s?.hata ? `Vekaletnameden doğum tarihi okunamadı: ${s.hata}` : 'Vekaletnamede doğum tarihi bulunamadı; elle girin.' });
      }
    } catch (e) {
      const yetki = e?.response?.status === 401 || e?.response?.status === 403;
      setDurum({ tip: 'uyari', metin: yetki
        ? 'Vekaletname okuma yalnızca yönetici hesabıyla yapılabiliyor; doğum tarihini elle girin.'
        : 'Vekaletname okunamadı; doğum tarihini elle girin.' });
    }
  };

  // Sayfa bir dosyadan açıldıysa (?caseFileId=) o dosya kendiliğinden seçilir — bir kez.
  const otomatik = useRef(false);
  useEffect(() => {
    if (otomatik.current || !baslangicId || !dosyalar?.length) return;
    const d = dosyalar.find((x) => String(al(x, 'Id')) === String(baslangicId));
    if (d) { otomatik.current = true; sec(d); }
    // sec her çizimde yeniden tanımlanıyor; yalnızca liste ve kimlik değişince çalışsın.
  }, [dosyalar, baslangicId]);

  const yakinlikDegis = (yeni) => {
    const eski = yakinlik;
    setYakinlik(yeni);
    // Müvekkilin doğum tarihi eski yakınlıktan yenisine taşınır.
    if (secili?.mod === 'destek' && secili.dogum) {
      onDoldur({ ...yakinlikTemizle(eski), ...dogumuYaz('destek', yeni, secili.dogum) });
    }
  };

  return (
    <section className="tdd">
      <div className="tdd-baslik">
        <span className="tdd-isaret" aria-hidden="true">✦</span>
        <div>
          <h2>Dosyadan doldur</h2>
          <p>Dosyayı seçin; olay tarihi, ad ve doğum tarihi (gerekirse vekaletnameden) gelsin.</p>
        </div>
      </div>

      {!secili ? (
        <div className="tdd-ara">
          <input
            type="search"
            className="tdd-girdi"
            placeholder="Föy no ya da müvekkil adı"
            aria-label="Dosya ara"
            value={ara}
            onFocus={() => setAcik(true)}
            onChange={(e) => { setAra(e.target.value); setAcik(true); }}
            onBlur={() => setTimeout(() => setAcik(false), 200)}
          />
          {acik && (
            <ul className="tdd-liste" role="listbox">
              {!dosyalar?.length && <li className="tdd-bos">Dosyalar yükleniyor…</li>}
              {dosyalar?.length > 0 && sonuclar.length === 0 && <li className="tdd-bos">Eşleşen dosya yok.</li>}
              {sonuclar.map((d) => (
                <li key={al(d, 'Id')}>
                  <button type="button" className="tdd-satir" onClick={() => sec(d)}>
                    <b>{al(d, 'FoyNo')}</b>
                    <span>{al(d, 'Muvekkil')}</span>
                    <small>{gun(al(d, 'KazaTarihi')) ? trTarih(gun(al(d, 'KazaTarihi'))) : ''}</small>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : (
        <div className="tdd-secili">
          <div className="tdd-secili-ust">
            <span>Föy <b>{secili.foyNo}</b> · {secili.muvekkil}</span>
            <button type="button" className="tdd-bag" onClick={() => { setSecili(null); setDurum(null); }}>Değiştir</button>
          </div>
          <div className="tdd-cipler">
            <span className="tdd-cip">{secili.mod === 'destek' ? 'Destek tazminatı (vefat dosyası)' : 'İş gücü kaybı'}</span>
            {secili.dogumKaynak === 'dosya' && <span className="tdd-cip">Doğum tarihi dosyadan</span>}
            {secili.dogumKaynak === 'vekaletname' && <span className="tdd-cip tdd-cip-altin">Doğum tarihi vekaletnameden</span>}
            <span className="tdd-cip tdd-cip-uyari">Cinsiyeti kontrol edin (yaşam tablosu değişir)</span>
          </div>
          {secili.mod === 'destek' && (
            <label className="tdd-yakinlik">
              <span>Müvekkil kazalının</span>
              <select value={yakinlik} onChange={(e) => yakinlikDegis(e.target.value)}>
                {YAKINLIK.map((y) => <option key={y.id} value={y.id}>{y.ad}</option>)}
              </select>
              <small>Kazalının (vefat edenin) doğum tarihini aşağıya elle girin.</small>
            </label>
          )}
        </div>
      )}

      {durum && (
        <p className={`tdd-durum tdd-durum-${durum.tip}`} role="status">
          {durum.tip === 'okunuyor' && <span className="tdd-donen" aria-hidden="true" />}
          {durum.metin}
        </p>
      )}
    </section>
  );
};

export default TazminatDosyaDoldur;
